import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FaCheckCircle,
    FaLock,
    FaPen,
    FaSave,
    FaStickyNote,
} from "react-icons/fa";

import Swal from "sweetalert2";

import api from "../api/api";

const STUDENTS_NOTE_STYLES = `
.cayep-students-note {
    width: 100%;
}

.cayep-students-note-form {
    overflow: hidden;
    background: #FFFFFF;
    border: 1px solid #E0E7EC;
    border-radius: 12px;
}

.cayep-students-note-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 15px;
    background: #F7F9FB;
    border-bottom: 1px solid #E0E7EC;
}

.cayep-students-note-toolbar > span:first-child {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #062A4B;
    font-size: 12px;
    font-weight: 800;
}

.cayep-students-note-toolbar svg {
    color: #C0750D;
}

.cayep-students-note-saved {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #4FAE22;
    font-size: 11px;
    font-weight: 800;
}

.cayep-students-note-saved svg {
    color: #4FAE22;
}

.cayep-students-note-form textarea {
    width: 100%;
    min-height: 280px;
    padding: 16px;
    border: 0;
    outline: none;
    resize: vertical;
    box-sizing: border-box;
    color: #142536;
    background: #FFFFFF;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.7;
}

.cayep-students-note-form textarea::placeholder {
    color: #9AA6B2;
}

.cayep-students-note-form textarea:disabled {
    background: #FAFBFC;
    cursor: not-allowed;
}

.cayep-students-note-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 15px;
    border-top: 1px solid #E0E7EC;
}

.cayep-students-note-footer > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #7A8793;
    font-size: 10px;
}

.cayep-students-note-footer button {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: 0;
    border-radius: 8px;
    padding: 9px 13px;
    background: #062A4B;
    color: #FFFFFF;
    cursor: pointer;
    font-family: inherit;
    font-size: 11px;
    font-weight: 800;
}

.cayep-students-note-footer button:hover {
    background: #063A6B;
}

.cayep-students-note-footer button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.cayep-students-note-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 30px;
    text-align: center;
    background: #F7F9FB;
    border: 1px dashed #D5DEE5;
    border-radius: 12px;
}

.cayep-students-note-empty > svg {
    margin-bottom: 12px;
    color: #0F6DAB;
    font-size: 27px;
}

.cayep-students-note-empty h4 {
    margin: 0 0 6px;
    color: #062A4B;
    font-size: 15px;
    font-weight: 800;
}

.cayep-students-note-empty p {
    max-width: 500px;
    margin: 0;
    color: #6B7280;
    font-size: 12px;
    line-height: 1.6;
}

@media (max-width: 480px) {
    .cayep-students-note-footer {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-students-note-footer button {
        width: 100%;
        justify-content: center;
    }

    .cayep-students-note-form textarea {
        min-height: 240px;
        padding: 14px;
    }
}
`;

const StudentsNote = ({
    course,
    classroom,
    setClassroom,
    isStudent,
    videoType = "introduction",
    videoSection = null,
    loadClassroom,
}) => {
    const [noteContent, setNoteContent] =
        useState("");

    const [noteSaved, setNoteSaved] =
        useState(false);

    const [savingNote, setSavingNote] =
        useState(false);

    const mountedRef =
        useRef(true);

    const noteSavedTimerRef =
        useRef(null);

    const courseId =
        course?.id;

    const isSectionVideo =
        videoType === "section" &&
        Boolean(
            videoSection?.id ||
                videoSection?.section_id ||
                videoSection?.sectionId
        );

    const sectionId =
        useMemo(() => {
            if (!isSectionVideo) {
                return null;
            }

            return (
                videoSection?.id ||
                videoSection?.section_id ||
                videoSection?.sectionId ||
                null
            );
        }, [
            isSectionVideo,
            videoSection,
        ]);

    const notesPath =
        useMemo(() => {
            if (!courseId) {
                return "";
            }

            if (
                isSectionVideo &&
                sectionId
            ) {
                return `api/users/subjects/${courseId}/video-sections/${sectionId}/notes/`;
            }

            return `api/users/subjects/${courseId}/notes/`;
        }, [
            courseId,
            isSectionVideo,
            sectionId,
        ]);

    const getErrorMessage =
        useCallback(
            (error, fallback) => {
                const data =
                    error?.response
                        ?.data;

                if (
                    typeof data ===
                    "string"
                ) {
                    return data;
                }

                if (data?.detail) {
                    return data.detail;
                }

                if (data?.message) {
                    return data.message;
                }

                if (data?.error) {
                    return data.error;
                }

                if (data?.content) {
                    return Array.isArray(
                        data.content
                    )
                        ? data.content.join(
                              " "
                          )
                        : data.content;
                }

                return fallback;
            },
            []
        );

    useEffect(() => {
        mountedRef.current =
            true;

        return () => {
            mountedRef.current =
                false;

            if (
                noteSavedTimerRef.current
            ) {
                clearTimeout(
                    noteSavedTimerRef.current
                );
            }
        };
    }, []);

    useEffect(() => {
        setNoteContent(
            classroom?.notes?.content ||
                ""
        );

        setNoteSaved(false);
    }, [
        classroom?.notes?.id,
        classroom?.notes?.content,
        courseId,
        sectionId,
    ]);

    const saveNote =
        useCallback(
            async (event) => {
                event.preventDefault();

                if (
                    !isStudent ||
                    !notesPath ||
                    savingNote
                ) {
                    return;
                }

                setSavingNote(
                    true
                );

                setNoteSaved(
                    false
                );

                const content =
                    noteContent;

                try {
                    const existingNote =
                        classroom?.notes;

                    let response;

                    if (
                        existingNote?.id
                    ) {
                        response =
                            await api.patch(
                                notesPath,
                                {
                                    content,
                                }
                            );
                    } else {
                        response =
                            await api.post(
                                notesPath,
                                {
                                    content,
                                }
                            );
                    }

                    const savedNote =
                        response?.data;

                    setClassroom(
                        (
                            previous
                        ) => ({
                            ...previous,
                            notes:
                                savedNote ||
                                {
                                    ...previous.notes,
                                    content,
                                },
                        })
                    );

                    setNoteContent(
                        savedNote?.content ??
                            content
                    );

                    setNoteSaved(
                        true
                    );

                    if (
                        noteSavedTimerRef.current
                    ) {
                        clearTimeout(
                            noteSavedTimerRef.current
                        );
                    }

                    noteSavedTimerRef.current =
                        window.setTimeout(
                            () => {
                                if (
                                    mountedRef.current
                                ) {
                                    setNoteSaved(
                                        false
                                    );
                                }
                            },
                            2500
                        );

                    if (
                        typeof loadClassroom ===
                        "function"
                    ) {
                        await loadClassroom(
                            false
                        );
                    }
                } catch (error) {
                    console.error(
                        "Unable to save note:",
                        error
                    );

                    Swal.fire({
                        icon: "error",
                        title:
                            "Note Not Saved",
                        text: getErrorMessage(
                            error,
                            "Your private note could not be saved."
                        ),
                        confirmButtonColor:
                            "#062A4B",
                    });
                } finally {
                    if (
                        mountedRef.current
                    ) {
                        setSavingNote(
                            false
                        );
                    }
                }
            },
            [
                isStudent,
                notesPath,
                savingNote,
                noteContent,
                classroom?.notes,
                setClassroom,
                loadClassroom,
                getErrorMessage,
            ]
        );

    return (
        <>
            <style>
                {
                    STUDENTS_NOTE_STYLES
                }
            </style>

            <section className="cayep-classroom-content-section cayep-students-note">
                <div className="cayep-classroom-section-heading">
                    <div className="cayep-classroom-heading-icon">
                        <FaStickyNote />
                    </div>

                    <div>
                        <span className="cayep-classroom-section-kicker">
                            PRIVATE
                            LEARNING
                        </span>

                        <h3>
                            My Notes
                        </h3>

                        <p>
                            Your notes are
                            private and
                            saved
                            specifically
                            for this
                            video.
                        </p>
                    </div>
                </div>

                {isStudent ? (
                    <form
                        className="cayep-students-note-form"
                        onSubmit={
                            saveNote
                        }
                    >
                        <div className="cayep-students-note-toolbar">
                            <span>
                                <FaPen />
                                Personal
                                notes
                            </span>

                            {noteSaved && (
                                <span className="cayep-students-note-saved">
                                    <FaCheckCircle />
                                    Saved
                                </span>
                            )}
                        </div>

                        <textarea
                            value={
                                noteContent
                            }
                            onChange={(
                                event
                            ) =>
                                setNoteContent(
                                    event
                                        .target
                                        .value
                                )
                            }
                            placeholder="Write your private notes for this video..."
                            rows={12}
                            disabled={
                                savingNote
                            }
                        />

                        <div className="cayep-students-note-footer">
                            <span>
                                <FaLock />
                                Only you
                                can view
                                these
                                notes.
                            </span>

                            <button
                                type="submit"
                                disabled={
                                    savingNote
                                }
                            >
                                <FaSave />

                                {savingNote
                                    ? "Saving..."
                                    : "Save Notes"}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="cayep-students-note-empty">
                        <FaLock />

                        <h4>
                            Student
                            Notes
                        </h4>

                        <p>
                            Notes are a
                            private
                            student
                            learning
                            feature.
                        </p>
                    </div>
                )}
            </section>
        </>
    );
};

export default StudentsNote;